import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Building2, AlertCircle, Search, X } from 'lucide-react';

const RequiredBadge = () => <span className="text-red-500 ml-1">*</span>;

// Type-to-search supplier picker (replaces the old dropdown, Oct 2026).
const SupplierSearchPicker = ({ suppliers, value, onSelect, disabled }) => {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const selected = suppliers.find((s) => s.id === value);

  React.useEffect(() => {
    const onClickAway = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (q
    ? suppliers.filter((s) =>
        (s.name || '').toLowerCase().includes(q) || (s.gstin || '').toLowerCase().includes(q))
    : suppliers
  )
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
    .slice(0, 50);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 h-10 px-3 border border-blue-200 bg-blue-50 rounded-md" data-testid="po-supplier-selected">
        <span className="text-sm font-medium text-slate-800 truncate">
          {selected.name} {selected.gstin ? `(${selected.gstin})` : ''}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={() => { onSelect(''); setQuery(''); setOpen(false); }}
            className="text-slate-400 hover:text-red-500 shrink-0"
            data-testid="po-supplier-clear-btn"
            title="Change supplier"
          >
            <X size={15} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <Input
        value={query}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search supplier by name or GSTIN…"
        className="pl-9"
        data-testid="po-supplier-search-input"
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg" data-testid="po-supplier-suggestions">
          {matches.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-slate-400 italic">No suppliers match “{query}”</div>
          ) : (
            matches.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { onSelect(s.id); setOpen(false); setQuery(''); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-50 last:border-0"
                data-testid={`po-supplier-option-${s.id}`}
              >
                <span className="font-medium text-slate-800">{s.name}</span>
                {s.gstin && <span className="text-xs text-slate-500 ml-2">{s.gstin}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

function POHeaderTab({ formData, setFormData, suppliers, mandatoryFields, disabled }) {
  const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);
  
  function isRequired(field) {
    return mandatoryFields.includes(field);
  }
  
  return (
    <div className="space-y-6" data-testid="po-header-tab">
      {/* Supplier Selection */}
      <div className="space-y-2">
        <Label className="flex items-center">
          <Building2 className="w-4 h-4 mr-2" />
          Supplier <RequiredBadge />
        </Label>
        <SupplierSearchPicker
          suppliers={suppliers}
          value={formData.supplier_id}
          onSelect={(v) => setFormData({ ...formData, supplier_id: v })}
          disabled={disabled}
        />
        {selectedSupplier && (
          <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <p><strong>Address:</strong> {selectedSupplier.address}</p>
            {selectedSupplier.gstin && <p><strong>GSTIN:</strong> {selectedSupplier.gstin}</p>}
            {selectedSupplier.contact_person && <p><strong>Contact:</strong> {selectedSupplier.contact_person}</p>}
          </div>
        )}
      </div>
      
      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <Label className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            PO Date {isRequired('po_date') && <RequiredBadge />}
          </Label>
          <Input
            type="date"
            value={formData.po_date}
            onChange={e => setFormData({...formData, po_date: e.target.value})}
            disabled={disabled}
            data-testid="po-date-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Expected Delivery {isRequired('expected_delivery_date') && <RequiredBadge />}
          </Label>
          <Input
            type="date"
            value={formData.expected_delivery_date}
            onChange={e => setFormData({...formData, expected_delivery_date: e.target.value})}
            disabled={disabled}
            data-testid="po-delivery-date-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="flex items-center">Payment Type <RequiredBadge /></Label>
          <Select
            value={formData.payment_type || undefined}
            onValueChange={v => setFormData({...formData, payment_type: v})}
            disabled={disabled}
          >
            <SelectTrigger data-testid="po-payment-type-select" className={!formData.payment_type ? 'border-amber-300' : ''}>
              <SelectValue placeholder="Select Paid or Credit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paid" data-testid="po-payment-type-paid">Paid</SelectItem>
              <SelectItem value="credit" data-testid="po-payment-type-credit">Credit</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <Select 
            value={formData.status} 
            onValueChange={v => setFormData({...formData, status: v})}
            disabled={disabled}
          >
            <SelectTrigger data-testid="po-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent to Supplier</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Validation Warning */}
      {mandatoryFields.length > 0 && (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Required Fields</p>
            <p className="text-yellow-700">Fields marked with * are mandatory based on your organization's policy.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default POHeaderTab;
